import {messages} from '@smartmixin/i18n';
import {Box, Modal} from '@mui/material';
import {X} from '@phosphor-icons/react';
import {KeyboardEvent, useId, useState} from 'react';
import {useIntl} from 'react-intl';
import {IconButton} from '../../components/ui';
import manifest from '../help-images.json';

type HelpImageProps = {
  src?: string;
  alt?: string;
};

// Generated at prebuild by scripts/generate-articles.js from the PNGs in
// public/help-images/ — explicit dimensions prevent layout shift.
const dimensions = manifest as Record<string, {w: number; h: number}>;

/**
 * Renderer for `![alt](…)` markdown images in help articles.
 *
 * The URL path picks the treatment:
 * - `/help-images/web/…`    → rounded corners and a hairline border
 * - `/help-images/mobile/…` → pre-framed iPhone PNG, rendered bare
 * - anything else           → plain image, no lightbox
 *
 * Click (or Enter/Space) opens a fullscreen lightbox (a dialog named by the
 * alt, closed by Escape, a click on the image or its close button). All non-portal markup
 * is span-based because react-markdown nests images inside <p> elements.
 */
const HelpImage = ({src = '', alt = ''}: HelpImageProps) => {
  const {formatMessage} = useIntl();
  const [open, setOpen] = useState(false);
  const imageId = useId();

  const variant = src.startsWith('/help-images/web/')
    ? 'web'
    : src.startsWith('/help-images/mobile/')
      ? 'mobile'
      : null;

  if (!variant) {
    return (
      <img
        data-testid="help-image-fallback"
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        style={{maxWidth: '100%'}}
      />
    );
  }

  const size = dimensions[src];

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  };

  return (
    <>
      {/* The zoom control is named for what it does and DESCRIBED by the
          image's alt: role=button makes its children presentational, so an
          aria-label alone hid the alt (what the screenshot shows). */}
      <Box
        component="span"
        data-testid={`help-image-${variant}`}
        role="button"
        tabIndex={0}
        aria-label={formatMessage(messages.common.zoomImage)}
        aria-describedby={alt ? imageId : undefined}
        onClick={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'block',
          textAlign: 'center',
          my: 3,
          cursor: 'zoom-in',
          borderRadius: 1,
          '& img': {
            maxWidth: variant === 'mobile' ? 360 : '100%',
            width: variant === 'mobile' ? '100%' : undefined,
            height: 'auto',
            verticalAlign: 'middle',
            ...(variant === 'web' && {
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }),
          },
        }}>
        <img
          id={imageId}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          width={size?.w}
          height={size?.h}
        />
      </Box>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{backdrop: {sx: {bgcolor: 'scrim'}}}}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Box
          role="dialog"
          aria-modal="true"
          aria-label={alt || formatMessage(messages.common.zoomImage)}
          data-testid="help-image-lightbox"
          sx={{outline: 'none', position: 'relative', lineHeight: 0}}>
          <Box
            component="img"
            src={src}
            alt={alt}
            onClick={() => setOpen(false)}
            sx={{
              maxWidth: '92vw',
              maxHeight: '92vh',
              objectFit: 'contain',
              cursor: 'zoom-out',
              borderRadius: 1,
            }}
          />
          <Box sx={{position: 'absolute', top: 8, right: 8, lineHeight: 1}}>
            <IconButton
              aria-label={formatMessage(messages.common.close)}
              onClick={() => setOpen(false)}
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'border.control',
                '&:hover': {bgcolor: 'background.paper'},
              }}>
              <X aria-hidden size={18} />
            </IconButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default HelpImage;
